import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleaningServicesIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import { useTheme } from "@mui/material/styles";
import CronBuilderBox from "../../../components/CronBuilderBox";
import { getMutedPanelSx } from "../../../theme/panelStyles";
import { plentyApi } from "../../../services/api/jtl/plenty";

const BundlesTab = () => {
  const theme = useTheme();
  // Manual Cleanup State
  const [manualLoading, setManualLoading] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const [manualError, setManualError] = useState(null);

  // System Status State
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Cron State
  const [loading, setLoading] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({
    cron_expression: "0 30 5 * * *",
    is_cron_active: false,
    last_run: null,
    next_run: null,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setStatusLoading(true);
    try {
      // Load System Status
      const statusRes = await plentyApi.getSystemStatus();
      setSystemStatus(statusRes.data);

      // Load Cron Status
      const cronRes = await plentyApi.getCronStatus();
      setGlobalSettings((prev) => ({
        ...prev,
        is_cron_active: cronRes.data.active,
        cron_expression: cronRes.data.expression || prev.cron_expression,
        last_run: cronRes.data.last_run_at,
        next_run: cronRes.data.next_run_at,
      }));
    } catch (err) {
      console.error(err);
      setError("Failed to load status data");
    } finally {
      setLoading(false);
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleManualCleanup = async () => {
    setManualLoading(true);
    setManualResult(null);
    setManualError(null);
    try {
      const response = await plentyApi.clean_up_bundles();
      setManualResult(response.data);
    } catch (err) {
      console.error(err);
      setManualError(
        err.response?.data?.detail || "Failed to run bundle cleanup",
      );
    } finally {
      setManualLoading(false);
    }
  };

  const toggleMonitoring = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (globalSettings.is_cron_active) {
        await plentyApi.stopCronJob();
        setSuccess("Cron job stopped successfully");
      } else {
        await plentyApi.startCronJob(globalSettings.cron_expression);
        setSuccess("Cron job started successfully");
      }
      loadData();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          `Failed to ${
            globalSettings.is_cron_active ? "stop" : "start"
          } cron job`,
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCronSchedule = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await plentyApi.startCronJob(globalSettings.cron_expression);
      setSuccess("Cron schedule updated successfully");
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update schedule");
    } finally {
      setLoading(false);
    }
  };

  const isSystemReady = systemStatus?.status === "ready";

  return (
    <Box sx={{ p: 3 }}>
      {/* System Status Check */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" display="flex" alignItems="center">
              System Status
              {statusLoading ? (
                <CircularProgress size={20} sx={{ ml: 2 }} />
              ) : isSystemReady ? (
                <CheckCircleIcon color="success" sx={{ ml: 1 }} />
              ) : (
                <ErrorIcon color="error" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || statusLoading}
            >
              Refresh
            </Button>
          </Box>

          {systemStatus && (
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      JTL Credentials Active:
                    </Typography>
                    <Chip
                      label={
                        systemStatus.details.jtl_credentials_active
                          ? "Yes"
                          : "No"
                      }
                      color={
                        systemStatus.details.jtl_credentials_active
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      Plenty Credentials (Active JTL):
                    </Typography>
                    <Chip
                      label={
                        systemStatus.details.plenty_credentials_active
                          ? "Yes"
                          : "No"
                      }
                      color={
                        systemStatus.details.plenty_credentials_active
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
              {!isSystemReady && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  System is not ready for bundle cleanup. Please ensure both JTL
                  and Plenty credentials are configured and active.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Manual Cleanup Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                display="flex"
                alignItems="center"
              >
                <CleaningServicesIcon sx={{ mr: 1 }} /> Manual Bundle Cleanup
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Manually trigger the bundle cleanup process. This will identify
                bundles in Plenty, remove market assignments for specified
                markets, and delete corresponding SKUs in JTL.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleManualCleanup}
                disabled={manualLoading || !isSystemReady}
                startIcon={
                  manualLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PlayIcon />
                  )
                }
                fullWidth
              >
                {manualLoading ? "Running Cleanup..." : "Run Cleanup Now"}
              </Button>

              {manualError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {manualError}
                </Alert>
              )}

              {manualResult && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">
                    Cleanup Completed:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Bundles Found: {manualResult.bundles_found}</li>
                    <li>
                      Markets Deleted: {manualResult.deleted_markets_count}
                    </li>
                    <li>SKUs Deleted: {manualResult.deleted_skus_count}</li>
                  </ul>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cron Job Control Panel (Styled like Kaufland Tab) */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Monitoring Control</Typography>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "medium" }}
              >
                Automated Monitoring Schedule
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CronBuilderBox
                    mb={2}
                    sx={{
                      pointerEvents: !isSystemReady ? "none" : "auto",
                      opacity: !isSystemReady ? 0.6 : 1,
                      "& .cron_builder .row": { marginBottom: "15px" },
                      "& .cron_builder select, & .cron_builder input": {
                        padding: "4px 8px",
                      },
                    }}
                  >
                    <Cron
                      onChange={(e) =>
                        setGlobalSettings((prev) => ({
                          ...prev,
                          cron_expression: e,
                        }))
                      }
                      value={globalSettings.cron_expression}
                      showResultText={true}
                      showResultCron={true}
                      locale="en"
                    />
                  </CronBuilderBox>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant={
                        globalSettings.is_cron_active ? "outlined" : "contained"
                      }
                      color={
                        globalSettings.is_cron_active ? "error" : "success"
                      }
                      startIcon={
                        globalSettings.is_cron_active ? (
                          <StopIcon />
                        ) : (
                          <PlayIcon />
                        )
                      }
                      onClick={toggleMonitoring}
                      disabled={loading || !isSystemReady}
                      size="small"
                      sx={{ minWidth: "120px" }}
                    >
                      {globalSettings.is_cron_active ? "Stop" : "Start"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={updateCronSchedule}
                      disabled={
                        loading ||
                        !globalSettings.is_cron_active ||
                        !isSystemReady
                      }
                      size="small"
                      sx={{ minWidth: "100px" }}
                    >
                      Update
                    </Button>
                    {loading && <CircularProgress size={20} />}
                  </Box>
                </Grid>
              </Grid>

              <Box sx={getMutedPanelSx(theme)}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong>{" "}
                  {globalSettings.is_cron_active ? (
                    <span style={{ color: "green" }}>Active</span>
                  ) : (
                    <span style={{ color: "red" }}>Inactive</span>
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Current Cron:</strong>{" "}
                  {globalSettings.cron_expression || "N/A"}
                </Typography>
                {globalSettings.next_run && (
                  <Typography variant="body2">
                    <strong>Next Run:</strong>{" "}
                    {globalSettings.is_cron_active
                      ? new Date(globalSettings.next_run).toLocaleString()
                      : "-"}
                  </Typography>
                )}
                {globalSettings.last_run && (
                  <Typography variant="body2">
                    <strong>Last Run:</strong>{" "}
                    {new Date(globalSettings.last_run).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BundlesTab;
